/**
 * Les phases courantes d'un chantier, par métier.
 *
 * Le patron, le 17 septembre : « les phases d'avancement : il faut un menu
 * déroulant avec les phases les plus communes en fonction des chantiers /
 * métiers. Laisse la possibilité de le faire manuellement aussi. »
 *
 * Deux raisons, et la seconde compte plus que la première. Taper huit
 * intitulés à la main sur un téléphone, c'est ce qui fait qu'un chantier reste
 * **sans aucune phase** — et sans phase, l'avancement du chantier est inconnu,
 * donc le tableau de bord ne propose aucune situation à facturer. Ensuite,
 * trois orthographes de « Gros œuvre » rendent les chantiers incomparables :
 * une liste commune les aligne.
 *
 * **Des propositions, jamais une énumération.** La phase reste du texte libre
 * en base : un métier qui manque s'ajoute ici sans migration, et un intitulé
 * hors liste s'écrit à la main — c'est la règle déjà retenue pour le poste
 * d'un salarié et son rôle sur le chantier.
 *
 * L'ordre des listes est celui du chantier, du premier au dernier jour : c'est
 * lui que reprend « Ajouter les phases types », et il évite d'avoir à ranger
 * les phases après coup.
 */

/** Ce que `tenants.trade_type` peut valoir. Voir `register_new_tenant`. */
export type TradeType =
  | "peinture"
  | "maconnerie"
  | "plomberie"
  | "electricite"
  | "menuiserie"
  | "couverture"
  | "climatisation"
  | "paysagisme"
  | "tous_corps_etat"
  | "autre";

/**
 * Le déroulé d'un chantier tous corps d'état — et le repli de tout métier
 * qu'on ne connaît pas. Il vaut mieux qu'une liste vide : ces neuf étapes se
 * retrouvent sur presque tous les chantiers de rénovation.
 */
const GENERIQUE = [
  "Installation de chantier",
  "Démolition et dépose",
  "Gros œuvre",
  "Réseaux (plomberie, électricité)",
  "Isolation et cloisons",
  "Menuiseries",
  "Revêtements (sols et murs)",
  "Finitions",
  "Nettoyage et réception",
] as const;

const PAR_METIER: Record<TradeType, readonly string[]> = {
  maconnerie: [
    "Installation de chantier",
    "Terrassement",
    "Fondations",
    "Élévation des murs",
    "Plancher et dalle",
    "Ouvertures et linteaux",
    "Enduits",
    "Finitions et nettoyage",
  ],
  peinture: [
    "Installation et protections",
    "Préparation des supports",
    "Rebouchage et ponçage",
    "Impression (sous-couche)",
    "Peinture des plafonds",
    "Peinture des murs",
    "Boiseries et menuiseries",
    "Finitions et nettoyage",
  ],
  plomberie: [
    "Installation de chantier",
    "Dépose de l'existant",
    "Percements et saignées",
    "Alimentation en eau",
    "Évacuations",
    "Pose des appareils sanitaires",
    "Mise en eau et essais",
    "Finitions et nettoyage",
  ],
  electricite: [
    "Installation de chantier",
    "Dépose de l'existant",
    "Saignées et gaines",
    "Tirage des câbles",
    "Tableau électrique",
    "Appareillage (prises, interrupteurs)",
    "Luminaires",
    "Mise en service et conformité",
  ],
  menuiserie: [
    "Relevé et métré",
    "Commande ou fabrication",
    "Dépose de l'existant",
    "Pose des menuiseries",
    "Étanchéité et calfeutrement",
    "Quincaillerie et réglages",
    "Finitions et nettoyage",
  ],
  couverture: [
    "Installation de chantier et échafaudage",
    "Dépose de la couverture",
    "Charpente et voligeage",
    "Écran sous-toiture",
    "Pose de la couverture",
    "Zinguerie et gouttières",
    "Faîtage et solins",
    "Dépose de l'échafaudage et nettoyage",
  ],
  climatisation: [
    "Étude et implantation",
    "Percements et supports",
    "Pose des unités",
    "Liaisons frigorifiques",
    "Raccordements électriques",
    "Tirage au vide et charge",
    "Mise en service et réglages",
    "Essais et remise des documents",
  ],
  paysagisme: [
    "Préparation du terrain",
    "Terrassement et nivellement",
    "Réseaux et arrosage",
    "Maçonnerie paysagère",
    "Plantations",
    "Engazonnement",
    "Finitions et nettoyage",
  ],
  tous_corps_etat: GENERIQUE,
  autre: GENERIQUE,
};

/**
 * Les phases proposées pour un métier. Un métier inconnu — ou pas encore
 * renseigné — reçoit le déroulé générique plutôt qu'une liste vide : une liste
 * vide renverrait au clavier, c'est-à-dire au chantier sans phases.
 */
export function phasesForTrade(trade: string | null | undefined): readonly string[] {
  const cle = (trade ?? "").trim().toLowerCase();
  return PAR_METIER[cle as TradeType] ?? GENERIQUE;
}

/**
 * Celles qui restent à poser : les intitulés déjà présents sur le chantier
 * sortent de la liste. Proposer « Fondations » à un chantier qui en a déjà une
 * fabrique les doublons qu'on cherche justement à éviter.
 *
 * La comparaison ignore la casse et les espaces de bord — « fondations  » est
 * la même phase que « Fondations ».
 */
export function remainingPhases(
  trade: string | null | undefined,
  existantes: readonly { name: string }[],
): readonly string[] {
  const deja = new Set(existantes.map((p) => p.name.trim().toLowerCase()));
  return phasesForTrade(trade).filter((nom) => !deja.has(nom.toLowerCase()));
}
